'use client'

import { GetPaginatedPostsDocument, GetPaginatedPostsQuery, InputMaybe } from '@/graphql/generated/graphql'
import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import Spinner from './Spinner'
import { format, parseISO } from 'date-fns'
import { client } from '@/lib/client'
import { convertContent } from '@/utils/convertContent'

type Props = {
  initialData: GetPaginatedPostsQuery
  categorySlug?: InputMaybe<string>
  year?: InputMaybe<number>
}

export default function PaginatedPosts({ initialData, year, categorySlug }: Readonly<Props>) {
  const [data, setData] = useState<GetPaginatedPostsQuery>(initialData)
  const [after, setAfter] = useState<string | null | undefined>(initialData?.posts?.pageInfo.endCursor)
  const [loading, setLoading] = useState(false)

  async function handleClick() {
    setLoading(true)
    const moreData = await client.request(GetPaginatedPostsDocument, { after, categorySlug, year })

    setData((prevData) => {
      const prevEdges = prevData?.posts?.edges ?? []
      const moreEdges = moreData.posts?.edges ?? []

      if (moreData.posts) {
        setAfter(moreData.posts.pageInfo.endCursor)

        return {
          posts: {
            edges: [...prevEdges, ...moreEdges],
            pageInfo: moreData.posts.pageInfo,
          },
        }
      }

      return prevData
    })

    setLoading(false)
  }

  return (
    <>
      {data?.posts?.edges.map((post) => (
        <div key={post.node.id} className="mb-12">
          <Link href={`/${post.node.slug}`}>
            <h2 className="mb-3 inline text-3xl font-bold uppercase leading-8 text-white hover:underline">
              {post.node.title}
            </h2>
          </Link>
          {post.node.categories && (
            <div className="flex space-x-2 font-bold uppercase text-zinc-600">
              <div>
                <span className="mr-2">Kategorie</span>
                {post.node.categories?.edges.map((category, index, categories) => (
                  <span key={category.node.slug}>
                    <Link className="text-zinc-400 hover:text-white hover:underline" href={category.node.slug ?? '#'}>
                      {category.node.name}
                    </Link>
                    {index < categories.length - 1 && <span className="mx-1">/</span>}
                  </span>
                ))}
              </div>
              {post.node.date && <div>| {format(parseISO(post.node.date), 'dd.MM.yyyy')}</div>}
            </div>
          )}
          <div className="mt-3 flex">
            {post.node.content && <div>{convertContent(post.node.content)}</div>}
            {post.node.featuredImage?.node.sourceUrl && (
              <Image
                width={144}
                height={144}
                src={post.node.featuredImage?.node.sourceUrl}
                alt={post.node.title ?? 'Bild'}
                className="ml-4 h-auto w-12 self-start md:w-36"
              />
            )}
          </div>
        </div>
      ))}
      {data?.posts?.pageInfo.hasNextPage && (
        <button
          className="mb-12 flex w-full items-center justify-center gap-2 rounded-md border border-zinc-700 px-4 py-2 text-center text-white transition duration-300 ease-in-out hover:cursor-pointer hover:bg-zinc-800 disabled:cursor-default disabled:bg-transparent sm:w-auto"
          onClick={handleClick}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner /> lade...
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
              weitere Beiträge laden...
            </>
          )}
        </button>
      )}
    </>
  )
}
